import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { endpoints, baseUrl } from '../../api/api';
import AdminHeader from './components/AdminHeader';

interface FeedbackEntry {
  _id: string;
  rating: number;
  message: string;
  type: 'post_submission' | 'general';
  user: { name: string; email: string } | null;
  book: { title: string } | null;
  createdAt: string;
}

type SortKey = 'date_desc' | 'date_asc' | 'rating_desc' | 'rating_asc';

const Stars: React.FC<{ rating: number; size?: 'sm' | 'md' }> = ({ rating, size = 'sm' }) => {
  const cls = size === 'md' ? 'w-5 h-5' : 'w-3.5 h-3.5';
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <svg key={s} className={`${cls} ${s <= rating ? 'text-orange-400' : 'text-gray-200'}`} fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
};

const SortIcon: React.FC<{ active: boolean; dir: 'asc' | 'desc' }> = ({ active, dir }) => (
  <svg className={`w-3.5 h-3.5 ml-1 inline-block transition-colors ${active ? 'text-orange-500' : 'text-gray-300'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {dir === 'desc'
      ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />}
  </svg>
);

const FeedbackPage: React.FC = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<FeedbackEntry[]>([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [ratingFilter, setRatingFilter] = useState<number | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo]   = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('date_desc');

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('accessToken');
        const res = await axios.get(`${baseUrl}${endpoints.adminGetFeedback}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setFeedback(res.data.feedback || []);
      } catch {
        setFeedback([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filtered = useMemo(() => {
    let list = [...feedback];

    if (ratingFilter !== null) {
      list = list.filter((f) => f.rating === ratingFilter);
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      list = list.filter((f) => new Date(f.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((f) => new Date(f.createdAt) <= to);
    }

    list.sort((a, b) => {
      if (sortKey === 'date_desc') return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
      if (sortKey === 'date_asc')  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
      if (sortKey === 'rating_desc') return b.rating - a.rating;
      return a.rating - b.rating;
    });

    return list;
  }, [feedback, ratingFilter, dateFrom, dateTo, sortKey]);

  const avg = feedback.length
    ? feedback.reduce((s, f) => s + f.rating, 0) / feedback.length
    : 0;

  const dist = [5, 4, 3, 2, 1].map((star) => ({
    star,
    count: feedback.filter((f) => f.rating === star).length,
  }));

  const toggleSort = (base: 'date' | 'rating') => {
    const descKey = `${base}_desc` as SortKey;
    const ascKey  = `${base}_asc`  as SortKey;
    setSortKey((prev) => (prev === descKey ? ascKey : descKey));
  };

  const hasFilters = ratingFilter !== null || dateFrom || dateTo;

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader onLogout={handleLogout} title="Feedback" />

      <main className="max-w-6xl mx-auto px-4 py-8 space-y-6">

        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-5 flex items-center gap-4">
            <p className="text-5xl font-bold text-orange-500 tabular-nums leading-none">
              {avg ? avg.toFixed(1) : '—'}
            </p>
            <div>
              <Stars rating={Math.round(avg)} size="md" />
              <p className="text-xs text-gray-400 mt-1.5">{feedback.length} total response{feedback.length !== 1 ? 's' : ''}</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-4 col-span-1 sm:col-span-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Rating breakdown</p>
            <div className="space-y-1.5">
              {dist.map(({ star, count }) => (
                <button
                  key={star}
                  onClick={() => setRatingFilter(ratingFilter === star ? null : star)}
                  className={`w-full flex items-center gap-2 rounded px-1 py-0.5 transition-colors ${ratingFilter === star ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
                >
                  <span className="text-xs text-gray-500 w-3 text-right">{star}</span>
                  <svg className="w-3 h-3 text-orange-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <div className="flex-1 bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${ratingFilter === star ? 'bg-orange-500' : 'bg-orange-400'}`}
                      style={{ width: feedback.length ? `${(count / feedback.length) * 100}%` : '0%' }}
                    />
                  </div>
                  <span className="text-xs text-gray-400 w-6 text-right tabular-nums">{count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filters bar */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-5 py-3.5 flex flex-wrap items-center gap-3">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Filters</span>

          {/* Star buttons */}
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((s) => (
              <button
                key={s}
                onClick={() => setRatingFilter(ratingFilter === s ? null : s)}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium border transition-colors ${
                  ratingFilter === s
                    ? 'bg-orange-500 text-white border-orange-500'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-orange-300 hover:text-orange-600'
                }`}
              >
                {s}
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </button>
            ))}
          </div>

          <div className="w-px h-5 bg-gray-200" />

          {/* Date range */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400">From</label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
            <label className="text-xs text-gray-400">To</label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="text-sm border border-gray-200 rounded-md px-2.5 py-1 focus:outline-none focus:ring-2 focus:ring-orange-400"
            />
          </div>

          {hasFilters && (
            <button
              onClick={() => { setRatingFilter(null); setDateFrom(''); setDateTo(''); }}
              className="ml-auto text-xs text-gray-400 hover:text-red-500 transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-800">
              {filtered.length} response{filtered.length !== 1 ? 's' : ''}
              {hasFilters && <span className="text-gray-400 font-normal"> (filtered)</span>}
            </h2>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-gray-400" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-gray-400 text-sm py-16">No responses match the current filters.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100 text-left">
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <button onClick={() => toggleSort('rating')} className="flex items-center hover:text-gray-800 transition-colors">
                      Rating
                      <SortIcon active={sortKey.startsWith('rating')} dir={sortKey === 'rating_asc' ? 'asc' : 'desc'} />
                    </button>
                  </th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Message</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Book</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <button onClick={() => toggleSort('date')} className="flex items-center hover:text-gray-800 transition-colors">
                      Date
                      <SortIcon active={sortKey.startsWith('date')} dir={sortKey === 'date_asc' ? 'asc' : 'desc'} />
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filtered.map((f) => (
                  <tr key={f._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3.5"><Stars rating={f.rating} /></td>
                    <td className="px-5 py-3.5 text-gray-700 max-w-xs">
                      {f.message
                        ? <span className="line-clamp-2">{f.message}</span>
                        : <span className="text-gray-300 italic text-xs">No message</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      {f.user ? (
                        <div>
                          <p className="text-gray-800 font-medium">{f.user.name}</p>
                          <p className="text-gray-400 text-xs">{f.user.email}</p>
                        </div>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5 text-gray-600 max-w-[160px] truncate">
                      {f.book?.title ?? <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-5 py-3.5">
                      <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        f.type === 'post_submission' ? 'bg-blue-50 text-blue-600' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {f.type === 'post_submission' ? 'Post Submit' : 'General'}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 text-gray-400 text-xs whitespace-nowrap">
                      {new Date(f.createdAt).toLocaleDateString('en-IN', {
                        day: 'numeric', month: 'short', year: 'numeric',
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
};

export default FeedbackPage;
